import { Euler, Matrix4, Quaternion, Vector3 } from 'three';
import type { Material, Object3D } from 'three';
import type { QualityPreset } from '@cloudpearl/config';
import { DECOR_CELL, OBSTACLE_CELL, decorForCell } from '@cloudpearl/core';
import type { Gate, Sim } from '@cloudpearl/core';
import type { MaterialSlot, PrefabKind, PrefabLibrary } from '@cloudpearl/assets';
import { InstancePool } from './InstancePool';
import type { InstanceHandle } from './InstancePool';

/** Kinds visible from very far away: streamed in a wider ring than everything else. */
const FAR_KINDS = new Set<PrefabKind>(['skyIsland', 'mesa']);

interface PartPools {
  near: InstancePool;
  far: InstancePool;
}

interface Placed {
  parts: PartPools[];
  handles: InstanceHandle[];
  band: 'near' | 'far';
  matrix: Matrix4;
}

interface CellState {
  ci: number;
  cj: number;
  /** 1 = far kinds only, 2 = everything. */
  level: 1 | 2;
  far: Placed[];
  rest: Placed[];
  band: 'near' | 'far';
}

const _m = new Matrix4();
const _q = new Quaternion();
const _e = new Euler();
const _p = new Vector3();
const _s = new Vector3();

/**
 * Instanced scenery: obstacles & landmarks (from the sim's deterministic field), decor and wind
 * gates. One draw call per prefab part per LOD band; cells hop between the near band (full
 * foliage, casts shadows) and the far band (thinned leaf cards, no shadows) as you move.
 */
export class Props {
  private readonly pools = new Map<string, PartPools[]>();
  private readonly cells = new Map<number, CellState>();
  private readonly gates = new Map<number, InstanceHandle>();
  private readonly gatePool: InstancePool;
  private gateTimer = 0;
  private readonly gateScratch: Gate[] = [];
  private readonly gateSeen = new Set<number>();
  private offsets: Int16Array | null = null;
  private scanX = Infinity;
  private scanZ = Infinity;
  private scanDone = false;

  constructor(
    parent: Object3D,
    prefabs: PrefabLibrary,
    materials: Map<MaterialSlot, Material>,
    foliageDepth: Material,
    gateGeometry: import('three').BufferGeometry,
    gateMaterial: Material,
    private sim: Sim,
    private readonly quality: QualityPreset,
  ) {
    for (const [kind, variants] of prefabs) {
      variants.forEach((v, vi) => {
        const pools = v.parts.map((part) => {
          const mat = materials.get(part.slot)!;
          const depth = part.slot === 'foliage' ? foliageDepth : undefined;
          const near = new InstancePool(parent, part.geometry, mat, part.castShadow && quality.shadows, depth);
          const far = new InstancePool(parent, part.far ?? part.geometry, mat, false, depth);
          if (part.slot === 'waterfall') near.mesh.renderOrder = far.mesh.renderOrder = 5;
          return { near, far };
        });
        this.pools.set(`${kind}:${vi}`, pools);
      });
    }
    this.gatePool = new InstancePool(parent, gateGeometry, gateMaterial, false);
    this.gatePool.mesh.renderOrder = 6;
  }

  setSim(sim: Sim): void {
    this.sim = sim;
    this.clear();
  }

  private place(kind: PrefabKind, variant: number, x: number, y: number, z: number, yaw: number, scale: number, band: 'near' | 'far'): Placed | null {
    const pools = this.pools.get(`${kind}:${variant}`) ?? this.pools.get(`${kind}:0`);
    if (!pools) return null;
    _p.set(x, y, z);
    _e.set(0, yaw, 0);
    _q.setFromEuler(_e);
    _s.setScalar(scale);
    const matrix = new Matrix4().compose(_p, _q, _s);
    return { parts: pools, handles: pools.map((p) => p[band].add(matrix)), band, matrix };
  }

  private remove(pl: Placed): void {
    pl.parts.forEach((p, i) => p[pl.band].remove(pl.handles[i]!));
  }

  private rebind(pl: Placed, band: 'near' | 'far'): void {
    if (pl.band === band) return;
    this.remove(pl);
    pl.band = band;
    pl.handles = pl.parts.map((p) => p[band].add(pl.matrix));
  }

  private loadCell(ci: number, cj: number, level: 1 | 2, band: 'near' | 'far', existing?: CellState): CellState {
    const c: CellState = existing ?? { ci, cj, level, far: [], rest: [], band };
    const loadFar = !existing;
    const loadRest = level === 2 && (!existing || existing.level === 1);
    const world = this.sim.world;
    if (loadFar || loadRest) {
      // The far ring only shows the far kinds: generate just those (the full cell — every hazard
      // checked against the terrain — is three quarters of the ring and was thrown away).
      const farOnly = level === 1;
      const obstacles = farOnly ? this.sim.field.giantsOf(ci, cj) : this.sim.field.cell(ci, cj);
      for (const o of obstacles) {
        const isFar = FAR_KINDS.has(o.kind);
        if ((isFar && !loadFar) || (!isFar && !loadRest)) continue;
        // Big hazards sit slightly into slopes so they never float.
        const pl = this.place(o.kind, o.variant, o.x, o.y - 0.08 * o.scale, o.z, Math.atan2(-o.s, o.c), o.scale, isFar ? 'far' : c.band);
        if (pl) (isFar ? c.far : c.rest).push(pl);
      }
      for (const d of decorForCell(world, ci, cj, this.quality.decor, farOnly ? FAR_KINDS : undefined)) {
        const isFar = FAR_KINDS.has(d.kind);
        if ((isFar && !loadFar) || (!isFar && !loadRest)) continue;
        const pl = this.place(d.kind, d.variant, d.x, d.y, d.z, d.yaw, d.scale, isFar ? 'far' : c.band);
        if (pl) (isFar ? c.far : c.rest).push(pl);
      }
    }
    c.level = level;
    return c;
  }

  private unloadRest(c: CellState): void {
    for (const pl of c.rest) this.remove(pl);
    c.rest.length = 0;
    c.level = 1;
  }

  /** Cell offsets around the centre cell, nearest first (built once; the ring never changes). */
  private ringOffsets(): Int16Array {
    if (this.offsets) return this.offsets;
    const R = Math.ceil((this.quality.viewDistance * 1.25) / OBSTACLE_CELL) + 1;
    const list: [number, number][] = [];
    for (let dj = -R; dj <= R; dj++) for (let di = -R; di <= R; di++) list.push([di, dj]);
    list.sort((a, b) => a[0] * a[0] + a[1] * a[1] - (b[0] * b[0] + b[1] * b[1]));
    this.offsets = new Int16Array(list.flat());
    return this.offsets;
  }

  private scan(x: number, z: number, budgetMs: number): boolean {
    const C = OBSTACLE_CELL;
    if (C !== DECOR_CELL) throw new Error('cell sizes differ');
    const near = this.quality.propDistance;
    const far = this.quality.viewDistance * 1.25;
    const bandR = this.quality.propNear;
    const t0 = performance.now();
    let complete = true;
    const cx = Math.floor(x / C);
    const cz = Math.floor(z / C);
    const off = this.ringOffsets();
    // Nearest cells first.
    for (let k = 0; k < off.length; k += 2) {
      const ci = cx + off[k]!;
      const cj = cz + off[k + 1]!;
      const d = Math.hypot((ci + 0.5) * C - x, (cj + 0.5) * C - z);
      if (d >= far) continue;
      const key = (ci + 32768) * 65536 + (cj + 32768);
      const level: 1 | 2 = d < near ? 2 : 1;
      const band: 'near' | 'far' = d < bandR ? 'near' : 'far';
      const c = this.cells.get(key);
      if (c && c.level >= level) {
        // LOD band switch with hysteresis.
        const hyst = c.band === 'near' ? bandR + 12 : bandR - 12;
        const nb: 'near' | 'far' = d < hyst ? 'near' : 'far';
        if (nb !== c.band) {
          c.band = nb;
          for (const pl of c.rest) this.rebind(pl, nb);
        }
        continue;
      }
      if (performance.now() - t0 > budgetMs) {
        complete = false;
        continue;
      }
      this.cells.set(key, this.loadCell(ci, cj, level, band, c));
    }
    // Unload.
    for (const [key, c] of this.cells) {
      const d = Math.hypot((c.ci + 0.5) * C - x, (c.cj + 0.5) * C - z);
      if (d > far + C * 1.5) {
        this.unloadRest(c);
        for (const pl of c.far) this.remove(pl);
        this.cells.delete(key);
      } else if (c.level === 2 && d > near + C * 1.5) this.unloadRest(c);
    }
    this.sim.field.prune();
    return complete;
  }

  /** Stream cells around (x, z). Spends at most ~`budgetMs` generating new cells. */
  update(x: number, z: number, dt: number, budgetMs = 3): boolean {
    // The ring only changes as you move: rescan every few metres (or until a budget-limited
    // pass has caught up), not every frame.
    if (!this.scanDone || Math.hypot(x - this.scanX, z - this.scanZ) > 4) {
      this.scanX = x;
      this.scanZ = z;
      this.scanDone = this.scan(x, z, budgetMs);
    }
    // Wind gates (cheap; refreshed a few times a second).
    this.gateTimer -= dt;
    if (this.gateTimer <= 0) {
      this.gateTimer = 0.25;
      const seen = this.gateSeen;
      seen.clear();
      for (const g of this.sim.gates.near(x, z, this.quality.propDistance, this.gateScratch)) {
        seen.add(g.id);
        if (this.gates.has(g.id)) continue;
        _p.set(g.x, g.y, g.z);
        _e.set(0, Math.atan2(g.nx, g.nz), 0);
        _q.setFromEuler(_e);
        _s.setScalar(g.r);
        _m.compose(_p, _q, _s);
        this.gates.set(g.id, this.gatePool.add(_m));
      }
      for (const [id, h] of this.gates) {
        if (!seen.has(id)) {
          this.gatePool.remove(h);
          this.gates.delete(id);
        }
      }
    }

    for (const pools of this.pools.values()) {
      for (const p of pools) {
        p.near.flush();
        p.far.flush();
      }
    }
    this.gatePool.flush();
    return this.scanDone;
  }

  /** Show every pool (even empty ones) so the shader warm-up compiles them all up front. */
  warmup(on: boolean): void {
    for (const pools of this.pools.values()) {
      for (const p of pools) {
        p.near.warmup(on);
        p.far.warmup(on);
      }
    }
    this.gatePool.warmup(on);
  }

  clear(): void {
    for (const c of this.cells.values()) {
      for (const pl of c.rest) this.remove(pl);
      for (const pl of c.far) this.remove(pl);
    }
    this.cells.clear();
    for (const h of this.gates.values()) this.gatePool.remove(h);
    this.gates.clear();
    this.gateTimer = 0;
    this.scanDone = false;
  }

  dispose(): void {
    this.clear();
    for (const pools of this.pools.values()) {
      for (const p of pools) {
        p.near.dispose();
        p.far.dispose();
      }
    }
    this.gatePool.dispose();
  }
}
